<!--
  PreferredDriverField: the driver's phone number or number plate. Shown
  once the customer adds the "+ Preferred driver" extra on the Details step
  (its × clears the value). Behind ORDER_FEATURES.preferredDriver. The
  Details step keeps this mounted (hidden) while the extra is closed, so the
  lookup answers below are kept for the visit, as with the old No / Yes.

  While the customer types, orders-service is asked (debounced) whether that
  driver can be reserved for this vehicle. It answers with a first name or
  one "can't reserve" message, never the phone, plate or surname. The answer
  is only a heads-up: nothing here blocks Confirm, and createOrder checks
  again and tells the customer if the driver couldn't be reserved.

  The parent owns the value (v-model, "" = none) and clears it when the
  vehicle changes. Coming back to Details checks the value again.

  Dedicated (`dedicated`): the driver is stored for Xobo ops to see (orders
  keeps it as a hint) and never reserved or offered, so the copy says it is
  passed on to ops instead of the 25 second offer. The lookup still runs.
-->
<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import axios from "axios";
import { authHeaders } from "../../composables/useCompanyKyc";
import {
  type PreferredLookupView,
  compactPreferredInput,
  preferredInputKind,
  preferredLookupView,
  preferredLookupErrorView,
  PREFERRED_INPUT_HINT,
  PREFERRED_DEDICATED_HELP,
} from "../../utils/preferredDriver";

const props = defineProps<{
  modelValue: string;
  /** The chosen quote's vehicle type; the lookup checks the driver against it. */
  vehicleTypeId: number;
  /** A Dedicated booking: the driver is passed on to ops, not offered. */
  dedicated?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const DEBOUNCE_MS = 600;

const text = computed({
  get: () => props.modelValue,
  set: (v: string) => emit("update:modelValue", v),
});

const checking = ref(false);
const result = ref<PreferredLookupView | null>(null);
// Shown once the customer has left the box or paused on a value that isn't
// a phone or a plate.
const shapeError = ref(false);

// One lookup per value and vehicle: a repeat answer comes from here, so
// going back and forth doesn't spend the customer's lookups.
const answered = new Map<string, PreferredLookupView>();
let timer: ReturnType<typeof setTimeout> | undefined;
let seq = 0;

function reset() {
  if (timer) clearTimeout(timer);
  timer = undefined;
  seq++;
  checking.value = false;
  result.value = null;
  shapeError.value = false;
}

async function lookup() {
  const raw = props.modelValue.trim();
  if (!raw) return;
  if (!preferredInputKind(raw)) {
    shapeError.value = true;
    return;
  }
  const key = `${props.dedicated ? "d" : "o"}:${props.vehicleTypeId}:${compactPreferredInput(raw)}`;
  const known = answered.get(key);
  if (known) {
    result.value = known;
    return;
  }
  const mine = ++seq;
  checking.value = true;
  let view: PreferredLookupView;
  try {
    const res = await axios.post(`${import.meta.env.VITE_ORDERS_SERVICE}preferred_driver/lookup`, {
      value: raw,
      vehicle_type: props.vehicleTypeId,
    }, { headers: authHeaders() });
    view = preferredLookupView(res.data, !!props.dedicated);
  } catch (err) {
    view = preferredLookupErrorView(err);
  }
  if (mine !== seq) return; // the value changed while this was out
  checking.value = false;
  result.value = view;
  // Only definite answers are kept; "can't check" may pass in a minute.
  if (view.kind !== "error") answered.set(key, view);
}

function schedule() {
  reset();
  if (!props.modelValue.trim()) return;
  timer = setTimeout(() => {
    timer = undefined;
    void lookup();
  }, DEBOUNCE_MS);
}

watch(() => props.modelValue, schedule);
watch(() => props.vehicleTypeId, schedule);
onMounted(() => {
  if (props.modelValue.trim()) void lookup();
});
onBeforeUnmount(reset);

function onBlur() {
  const raw = props.modelValue.trim();
  if (raw && !preferredInputKind(raw)) shapeError.value = true;
}

const input = ref<{ focus: () => void } | null>(null);
defineExpose({ focus: () => input.value?.focus() });

const status = computed(() => {
  if (shapeError.value) return { kind: "invalid", text: PREFERRED_INPUT_HINT };
  if (checking.value) return { kind: "checking", text: "Checking…" };
  return result.value;
});
</script>

<template>
  <div class="pdf">
    <label class="ods-label pdf__input-label" for="pdf-input">Driver's phone number or number plate</label>
    <el-input
      id="pdf-input"
      ref="input"
      v-model="text"
      :maxlength="20"
      placeholder="e.g. 0712 345 678 or KDA 123A"
      autocomplete="off"
      class="ods-input"
      :class="{ 'is-invalid': status?.kind === 'invalid' }"
      aria-describedby="pdf-status"
      @blur="onBlur"
    />
    <p
      id="pdf-status"
      role="status"
      :class="{
        'ods-error': status?.kind === 'invalid',
        'pdf__ok': status?.kind === 'available',
        'ods-help': !status || ['checking', 'unavailable', 'error'].includes(status.kind),
      }"
    >
      {{ status ? status.text : (dedicated ? PREFERRED_DEDICATED_HELP : "Someone who has delivered for you before. They get the order first.") }}
    </p>
  </div>
</template>

<style scoped src="./order-form.css"></style>
<style scoped>
.pdf { font-family: 'DM Sans', sans-serif; }

.pdf__ok {
  margin: 6px 0 0;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.74rem;
  line-height: 1.45;
  font-weight: 600;
  color: #047857;
}
</style>
